import React from 'react'
import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { setSelectedTier } from '@paintscout/util/builder'
import type { TieredPackageTierId } from 'paintscout'
import { useQuote } from '../../../../context'
import GatePreviewHeader from './GatePreviewHeader'
import GatePreviewFooter from './GatePreviewFooter'
import PackageComparisonCards from './PackageComparisonCards'

const useStyles = makeStyles<Theme>(
  (theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(4)
      },
      heading: {
        color: neutral.P30,
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(16),
        marginBottom: theme.spacing(2)
      }
    }
  },
  { name: 'TieredPackageGate' }
)

export interface TieredPackageGateProps {
  /**
   * Called after a tier has been selected (its "View" button clicked). The gate applies the tier
   * client-side via the surrounding quote context; the caller advances past the gate to the estimate.
   */
  onViewTier: (tierId: TieredPackageTierId) => void
}

/**
 * Customer-facing tiered-package "gate" — shown in the estimate's place in the presentation until the
 * customer picks a package (see {@link PackageGateOrEstimate}). Presents Good/Better/Best as
 * comparison cards (via the shared {@link PackageComparisonCards}) between a customer header and
 * footer. Selecting a tier applies it (client-side recompute) and hands the id back via
 * `onViewTier`, which swaps the estimate back in.
 */
export default function TieredPackageGate({ onViewTier }: TieredPackageGateProps) {
  const classes = useStyles({})
  const { quote, updateQuote } = useQuote()
  const { options, rates } = useClientOptions()

  // Apply the chosen tier to the (preview/customer) quote — updateQuote is local-only in this context
  // (see QuoteContextWrapper), so this recomputes totals without persisting — then advance.
  const handleView = (tierId: TieredPackageTierId) => {
    updateQuote({ quote: setSelectedTier({ quote, options, rates, tierId }) })
    onViewTier(tierId)
  }

  if (!quote.tieredPackages?.enabled) {
    return null
  }

  return (
    <div className={classes.root}>
      <GatePreviewHeader />
      <Typography className={classes.heading}>Choose a package that works best for you.</Typography>
      <PackageComparisonCards variant="gate" onSelectTier={handleView} />
      <GatePreviewFooter />
    </div>
  )
}
